/* eslint-disable react/require-default-props */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faCircleQuestion,
} from '@fortawesome/sharp-solid-svg-icons';
import React, {
  ForwardedRef,
  InputHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { GLOBAL_TOOLTIP } from '@/constants/constants';

import Typography, { TypographyVariant } from '../typography';

type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
  className?: string;
  inputClassName?: string;
  indeterminate?: boolean;
  label?: string;
  labelClassName?: string;
  moreInfo?: string;
  infoIcon?: boolean;
  labelVariant?: TypographyVariant;
};

const Checkbox = React.forwardRef(
  (
    {
      indeterminate,
      moreInfo,
      className = undefined,
      inputClassName = undefined,
      label = '',
      labelClassName = undefined,
      infoIcon = false,
      labelVariant = 'body1',
      ...props
    }: CheckboxProps,
    forwardedRef: ForwardedRef<HTMLInputElement & { indeterminate: boolean }>
  ) => {
    const ref = useRef<HTMLInputElement>(null);

    // This combines the refs
    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    useEffect(() => {
      if (!ref.current) return;

      ref.current.indeterminate = !!indeterminate;
    }, [indeterminate]);

    return (
      <div
        className={twMerge(
          'checkbox-selector flex items-center space-x-3 text-white',
          className
          // To set the border color to some other color, set the text color to your color here
          //  this is becuase of the `currentcolor` usage in the checkbox-selector classname
        )}
      >
        <input
          ref={ref}
          className={twMerge(
            'text-white checked:bg-green-50 disabled:opacity-60',
            'border-[2px] border-gray-100 checked:border-green-50 indeterminate:border-green-50 indeterminate:bg-green-50',
            props.disabled ? 'cursor-auto' : 'cursor-pointer',
            inputClassName
          )}
          id={label}
          type="checkbox"
          {...props}
        />
        {label && (
          <label
            className={twMerge(
              'select-none',
              props.disabled ? 'cursor-auto' : 'cursor-pointer'
            )}
            htmlFor={label}
          >
            <Typography
              className={twMerge(
                'text-gray-100',
                props.disabled ? 'opacity-60' : '',
                labelClassName
              )}
              variant={labelVariant}
            >
              {label}
            </Typography>
          </label>
        )}
        {moreInfo && infoIcon ? (
          <FontAwesomeIcon
            className="cursor-pointer text-gray-70 hover:text-gray-100"
            data-tooltip-content={moreInfo}
            data-tooltip-delay-show={300}
            data-tooltip-id={GLOBAL_TOOLTIP}
            icon={faCircleInfo}
          />
        ) : (
          moreInfo && (
            <FontAwesomeIcon
              className="cursor-pointer text-green-50"
              data-tooltip-content={moreInfo}
              data-tooltip-delay-show={300}
              data-tooltip-id={GLOBAL_TOOLTIP}
              icon={faCircleQuestion}
            />
          )
        )}
      </div>
    );
  }
);

export default Checkbox;
